import {ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, signal} from '@angular/core';
import {RequestStatusEnum} from '@dto/requests/request-status.enum';
import {RequestListViewModel} from '../view-models/request-list.view-model';
import {RequestViewModel} from '../view-models/request.view-model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {RequestService} from '../services/request.service';
import {RequestConverterService} from '../services/request-converter.service';
import {Router} from '@angular/router';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';

@Component({
    selector: 'app-request-list',
    templateUrl: './request-list.component.html',
    styleUrls: ['./request-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingAnimationComponent]
})
export class RequestListComponent implements OnInit {

  readonly requestStatus = input<RequestStatusEnum>();
  readonly noRequestsMessage = input<string>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly requestService = inject(RequestService);
  private readonly requestsConverterService = inject(RequestConverterService);
  private readonly router = inject(Router);

  readonly dataLoading = signal(true);
  readonly dataAvailable = signal(false);
  readonly dataEmpty = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly requests = signal<RequestListViewModel[]>([]);

  ngOnInit(): void {
    this.dataLoading.set(true);
    this.dataAvailable.set(false);
    this.getRequests();
  }

  getRequests(): void {
    this.errorMessage.set(null);
    this.requestService.getRequests(this.requestStatus()).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: requests => {
        this.requests.set(this.requestsConverterService.convertRequestListDtoToRequestListViewModels(requests));
        this.dataLoading.set(false);
        this.dataEmpty.set(this.requests().length === 0);
        this.dataAvailable.set(!this.dataEmpty());
      },
      error: () => {
        this.dataLoading.set(false);
        this.dataEmpty.set(false);
        this.dataAvailable.set(false);
        this.errorMessage.set('Failed to load requests. Please try again later.');
      }
    });
  }

  dismissError(): void {
    this.errorMessage.set(null);
  }

  retryLoad(): void {
    this.dataLoading.set(true);
    this.getRequests();
  }

  openRequest(request: RequestListViewModel): void {
    const summary: RequestViewModel = {
      id: request.id,
      status: request.status,
      readonly: request.readonly,
      description: request.description,
      percentageComplete: request.percentageComplete,
      originalTemplateId: request.originalTemplateId
    };
    this.router.navigate(request.detailsRouterLinkCommands, {
      state: { taskDetailViewModel: summary }
    });
  }

  cancelRequest(requestId: string): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    this.requestService.cancelRequest(requestId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.getRequests();
      },
      error: () => {
        this.dataLoading.set(false);
        this.dataEmpty.set(this.requests().length === 0);
        this.dataAvailable.set(!this.dataEmpty());
      }
    });
  }
}
